import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { useAuth } from './useAuth'
import { SDS_CANONICAL_SUPPLIER } from '@/config'
import { NewDataPoint } from '@coac-gmbh/saifty-phrases'
import { sessionExpired } from '@/api/axios'

type HttpMethod = 'GET' | 'POST'

interface EndpointPattern {
  method: HttpMethod
  regex: RegExp
}

interface IdTokenRule {
  name: string
  endpoints: EndpointPattern[]
  baseUrls: (string | undefined)[]
  condition?: (config: AxiosRequestConfig) => boolean
}

const FILE_STORAGE_ENDPOINTS: EndpointPattern[] = [
  // POST /filestorage/{fileId}/upload/external
  {
    method: 'POST',
    regex: /\/filestorage\/[^/]+\/upload\/external\/?$/,
  },

  // GET /filestorage/{fileId}
  {
    method: 'GET',
    regex: /\/filestorage\/[^/]+\/?$/,
  },
]

const PHRASES_ENDPOINTS: EndpointPattern[] = [
  // POST /phrases/smart-match
  {
    method: 'POST',
    regex: /\/phrases\/smart-match\/?$/,
  },
]

const ID_TOKEN_RULES: IdTokenRule[] = [
  {
    name: 'file-storage',
    endpoints: FILE_STORAGE_ENDPOINTS,
    baseUrls: [import.meta.env.VITE_API_SAIFTY_FILE_SYSTEM_BASE_URL, window.ENV.API_FILE_SYSTEM_BASE_URL],
  },
  {
    name: 'phrases-smart-match-with-supplier',
    endpoints: PHRASES_ENDPOINTS,
    baseUrls: [import.meta.env.VITE_API_SAIFTY_PHRASES_BASE_URL, window.ENV.API_PHRASES_BASE_URL],
    condition: (config: AxiosRequestConfig) => hasSmartMatchBodySupplierItem(config.data),
  },
]

export function configureAuthorizationHeaderInterceptor(axiosInstance: AxiosInstance, prefix = 'Bearer') {
  axiosInstance.interceptors.request.use(async (config) => {
    const auth = useAuth()
    config.headers = config.headers ?? {}

    if (!auth.isAuthenticated) return config

    const token = await auth.getAccessToken().catch(() => {
      sessionExpired()
    })
    config.headers.Authorization = `${prefix} ${token}`

    if (shouldAttachIdToken(config)) {
      config.headers['x-id-token'] = await auth.getIdToken()
    }

    return config
  })
}

function matchesEndpoint(
  method?: string,
  url?: string,
  patterns: EndpointPattern[] = [],
  baseUrls: (string | undefined)[] = [],
): boolean {
  if (!method || !url) return false

  const normalizedMethod = method.toUpperCase()
  const path = url.split('?')[0]

  const matchesBaseUrl = baseUrls.some((base) => !!base && url.startsWith(base))

  return matchesBaseUrl && patterns.some((pattern) => pattern.method === normalizedMethod && pattern.regex.test(path))
}

function shouldAttachIdToken(config: AxiosRequestConfig): boolean {
  return ID_TOKEN_RULES.some((rule) => {
    const matches = matchesEndpoint(config.method, config.url, rule.endpoints, rule.baseUrls)

    if (!matches) return false

    return rule.condition ? rule.condition(config) : true
  })
}

function hasSmartMatchBodySupplierItem(rawData: unknown): boolean {
  const payload = getRequestPayload(rawData)
  if (!Array.isArray(payload)) return false

  return (payload as NewDataPoint[]).some((item) => item?.data?.name === SDS_CANONICAL_SUPPLIER)
}

function getRequestPayload(data: unknown): unknown {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch {
      return null
    }
  }

  return data
}
